import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { applyPageMetadata, applyStructuredData } from '../utils/seo';

const faqs = [
  {
    question: 'Do I need extensive event experience to create a profile?',
    answer: 'No. You can create a profile at any experience level and describe the event work you have handled so far.',
  },
  {
    question: 'Can I specify event types I prefer?',
    answer: 'Yes. You can use profile fields to describe your preferred work style and event-related background.',
  },
  {
    question: 'How far in advance are event shifts arranged?',
    answer: 'Timelines vary by venue and event. Some opportunities are planned early and others happen on shorter notice.',
  },
  {
    question: 'Is event bartending available in every area?',
    answer: 'Availability depends on local platform activity. A complete profile keeps you visible as demand develops in your area.',
  },
  {
    question: 'Can I use BarsConnect if I only want event work?',
    answer: 'Yes. You can describe event-focused availability in your profile.',
  },
  {
    question: 'Is BarsConnect free to use?',
    answer: 'Yes. BarsConnect is free for bartenders during beta with no credit card required to sign up.',
  },
];

export default function EventBartenderJobsPage() {
  useEffect(() => {
    const cleanupMeta = applyPageMetadata({
      title: 'Event Bartender Jobs — Find Event Work on BarsConnect',
      description: 'Looking for event bartending work? Create a free BarsConnect profile and connect with bars and venues hiring for weddings, private events, and one-off bookings.',
      keywords: 'event bartender jobs, wedding bartender shifts, private event bartending, pop-up bar work',
      canonicalUrl: 'https://barsconnect.com/event-bartender-jobs',
      robots: 'index,follow',
    });

    const cleanupSchema = applyStructuredData([
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://barsconnect.com/' },
          { '@type': 'ListItem', position: 2, name: 'Event Bartender Jobs', item: 'https://barsconnect.com/event-bartender-jobs' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ]);

    return () => {
      cleanupMeta();
      cleanupSchema();
    };
  }, []);

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="event-bartender-jobs-page">
      <div className="max-w-5xl mx-auto">
        <header className="mb-10" data-testid="event-bartender-jobs-hero-section">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="event-bartender-jobs-title">Event Bartender Jobs — Get Hired for the Shifts That Matter</h1>
          <p className="text-muted-foreground mb-4" data-testid="event-bartender-jobs-hero-copy-1">
            Event bartending has unique demands. BarsConnect gives you a focused space to show event experience and connect with venues staffing weddings, private functions, and one-off bookings.
          </p>
          <p className="text-muted-foreground mb-6" data-testid="event-bartender-jobs-hero-copy-2">
            Create a free profile, highlight your event background, and connect directly through platform messaging.
          </p>
          <Button asChild data-testid="event-bartender-jobs-primary-cta-btn">
            <Link to="/register/bartenders">Create Free Bartender Profile</Link>
          </Button>
        </header>

        <section className="mb-10" data-testid="event-bartender-jobs-how-it-works-section">
          <h2 className="text-2xl font-semibold text-white mb-4" data-testid="event-bartender-jobs-how-it-works-heading">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-white/5 border border-white/10" data-testid="event-bartender-jobs-step-1-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="event-bartender-jobs-step-1-title">Step 1 — Highlight Event Experience</h3>
                <p className="text-sm text-muted-foreground" data-testid="event-bartender-jobs-step-1-copy">Describe the events and service environments you have worked so bars can evaluate fit faster.</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10" data-testid="event-bartender-jobs-step-2-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="event-bartender-jobs-step-2-title">Step 2 — Get Found by Hiring Venues</h3>
                <p className="text-sm text-muted-foreground" data-testid="event-bartender-jobs-step-2-copy">Bars and venues can browse profiles when they need event-ready bartenders for upcoming dates.</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10" data-testid="event-bartender-jobs-step-3-card">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold mb-2" data-testid="event-bartender-jobs-step-3-title">Step 3 — Review and Confirm</h3>
                <p className="text-sm text-muted-foreground" data-testid="event-bartender-jobs-step-3-copy">Discuss opportunity details through messaging and decide whether each event is the right match.</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="mb-10" data-testid="event-bartender-jobs-content-section">
          <h2 className="text-2xl font-semibold text-white mb-3" data-testid="event-bartender-jobs-content-heading">Why Event Experience Stands Out</h2>
          <p className="text-muted-foreground mb-4" data-testid="event-bartender-jobs-content-copy-1">
            Event service often requires adapting quickly to new venues, high-volume windows, and unfamiliar service layouts. A clear profile helps showcase this kind of practical experience.
          </p>
          <p className="text-muted-foreground" data-testid="event-bartender-jobs-content-copy-2">
            You can also browse <Link className="text-primary hover:underline" to="/bartender-jobs" data-testid="event-bartender-jobs-inline-link-general">general bartender jobs</Link>,
            {' '}<Link className="text-primary hover:underline" to="/part-time-bartender-jobs" data-testid="event-bartender-jobs-inline-link-part-time">part-time opportunities</Link>, and
            {' '}<Link className="text-primary hover:underline" to="/bartender-jobs-near-me" data-testid="event-bartender-jobs-inline-link-near-me">local opportunities near you</Link>.
          </p>
        </section>

        <section className="mb-10" data-testid="event-bartender-jobs-faq-section">
          <h2 className="text-2xl font-semibold text-white mb-4" data-testid="event-bartender-jobs-faq-heading">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((item, index) => (
              <Card key={item.question} className="bg-white/5 border border-white/10" data-testid={`event-bartender-jobs-faq-card-${index + 1}`}>
                <CardContent className="p-5">
                  <h3 className="text-white font-semibold mb-2" data-testid={`event-bartender-jobs-faq-question-${index + 1}`}>{item.question}</h3>
                  <p className="text-sm text-muted-foreground" data-testid={`event-bartender-jobs-faq-answer-${index + 1}`}>{item.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section data-testid="event-bartender-jobs-repeat-cta-section">
          <Card className="bg-white/5 border border-white/10" data-testid="event-bartender-jobs-repeat-cta-card">
            <CardContent className="p-6 sm:p-8">
              <h2 className="text-2xl font-semibold text-white mb-3" data-testid="event-bartender-jobs-repeat-cta-heading">Show your event experience where venues are hiring.</h2>
              <p className="text-muted-foreground mb-5" data-testid="event-bartender-jobs-repeat-cta-copy">Create a profile once and stay visible for future event opportunities.</p>
              <div className="flex flex-wrap gap-3">
                <Button asChild data-testid="event-bartender-jobs-repeat-cta-register-btn">
                  <Link to="/register/bartenders">Create Free Bartender Profile</Link>
                </Button>
                <Button asChild variant="outline" data-testid="event-bartender-jobs-repeat-cta-app-btn">
                  <Link to="/bartender-app">Explore the Bartender App Experience</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
